import moment from "moment";
import { DatePresetOption } from "@/common/enums/date-preset";
Object.assign(moment, { suppressDeprecationWarnings: true });
export class DateHelper {
    static formatString(value) {
        if (!value)
            return "";
        if (value.indexOf(":") != -1)
            return moment(value).format("DD MMM YYYY, HH:mm");
        return moment(value).format("DD MMM YYYY");
    }
    ;
    /**
    * Convert string to Date using moment(value).toDate()
    */
    static convertToDate(value) {
        return moment(value).toDate();
    }
    ;
    /**
     * Convert Date to moment string using moment(value).format()
     */
    static convertToMomentString(value) {
        if (!value)
            return "";
        return moment(value.toISOString()).format();
    }
    ;
    /**
     * Convert Date to moment string using moment(value).format()
     */
    static convertToFriendlyMomentTimeString(value) {
        if (!value)
            return "";
        return moment(value.toISOString()).format("DD MMM YYYY, HH:mm:ss");
    }
    ;
    /**
     * Convert Date to a relative string - "3 minutes ago", "2 days ago".
     *
     * For values that are only approximate, where "a few minutes ago" is the honest reading and an
     * exact timestamp would imply a precision the value does not have. Pair it with the absolute
     * time in a tooltip.
     */
    static convertToRelativeTimeString(value) {
        if (!value)
            return "";
        return moment(value.toISOString()).fromNow();
    }
    ;
    static convertToFriendlyMomentDateString(value) {
        if (!value)
            return "";
        return moment(value.toISOString()).format("DD MMM YYYY");
    }
    ;
    /**
     * Get the current date without time.
     * */
    static getCurrentDate() {
        let now = moment().toDate();
        now.setHours(0, 0, 0, 0);
        return now;
    }
    ;
    /**
     * Get the current date formatted as YYYY-MM-DD
     * */
    static getCurrentDateAsString() {
        return moment().format("YYYY-MM-DD");
    }
    ;
    /**
     * Get the current UTC date formatted as YYYY-MM-DD
     * */
    static getCurrentUTCDateAsString() {
        return moment().utc().format("YYYY-MM-DD");
    }
    ;
    /**
     * Get the current date/time formatted as YYYYMMDDHHmmss
     * */
    static getTimestamp() {
        return moment().format("YYYYMMDDHHmmss");
    }
    ;
    /**
     * Get date formatted as YYYYMMDDHHmmss. If date is undefined, will return the current timestamp.
     * */
    static getTimestampFromTime(date) {
        if (!date)
            return this.getTimestamp();
        return moment(date.toISOString()).format("YYYYMMDDHHmmss");
    }
    ;
    /**s
     * Get a date range consisting of a start and end date based on provided DatePresetOption
     */
    static getDateRange(datePreset) {
        let _start = null;
        let _end = null;
        let _now = new Date();
        _end = moment(_now.toISOString()).endOf("day").toDate();
        switch (datePreset) {
            case DatePresetOption.Default:
                _start = null;
                _end = null;
                break;
            case DatePresetOption.PrevMonth:
                _start = moment(_now.toISOString()).subtract(1, 'months').startOf('month').toDate();
                _end = moment(_now.toISOString()).subtract(1, 'months').endOf('month').toDate();
                break;
            case DatePresetOption.OneMonth:
                _start = moment(_now.toISOString()).add(-30, "days").startOf("day").toDate();
                break;
            case DatePresetOption.SevenDays:
                _start = moment(_now.toISOString()).add(-7, "days").startOf("day").toDate();
                break;
            case DatePresetOption.ThreeMonths:
                _start = moment(_now.toISOString()).add(-3, "months").startOf("day").toDate();
                break;
            case DatePresetOption.OneYear:
                _start = moment(_now.toISOString()).add(-1, "years").startOf("day").toDate();
                break;
        }
        return {
            start: _start,
            end: _end
        };
    }
    ;
    /**
     * Get a date in string format that is acceptable for a query parameter.
     * */
    static getDateForQueryString(value) {
        return moment(value.toISOString()).format(DateHelper.dateFormat);
    }
    ;
    static addDay(value, increment) {
        let date = value || new Date();
        let momentDate = moment(date);
        return momentDate.add("day", increment).toDate();
    }
    ;
    static addMonth(value, increment) {
        let date = value || new Date();
        let momentDate = moment(date);
        return momentDate.add("month", increment).toDate();
    }
    ;
    static isSameDate(value1, value2) {
        if (!value1 && !value2)
            return true;
        if (value1 && value2)
            return moment(value1).isSame(moment(value2), "day");
        return false;
    }
    ;
    static getTimezoneOffset() {
        return new Date().getTimezoneOffset();
    }
    ;
    static getStartOfDay(value) {
        return moment(value.toISOString()).startOf("day").toDate();
    }
    ;
    static getEndOfDay(value) {
        return moment(value.toISOString()).endOf("day").toDate();
    }
    ;
    static validateDateRange(start, end) {
        if (!start || !end)
            return true;
        let _start = this.convertToDate(`${start}`);
        let _end = this.convertToDate(`${end}`);
        return _start < _end;
    }
    ;
    static getChartJSDateAdapter() {
        const FORMATS = {
            datetime: 'MMM D, YYYY, h:mm:ss a',
            millisecond: 'h:mm:ss.SSS a',
            second: 'h:mm:ss a',
            minute: 'h:mm a',
            hour: 'hA',
            day: 'MMM D',
            week: 'll',
            month: 'MMM YYYY',
            quarter: '[Q]Q - YYYY',
            year: 'YYYY'
        };
        var output = typeof moment === 'function' ? {
            formats: function () {
                return FORMATS;
            },
            parse: function (value, format) {
                if (typeof value === 'string' && typeof format === 'string') {
                    value = moment(value, format);
                }
                else if (!(value instanceof moment)) {
                    value = moment(value);
                }
                return value.isValid() ? value.valueOf() : null;
            },
            format: function (time, format) {
                return moment(time).format(format);
            },
            add: function (time, amount, unit) {
                return moment(time).add(amount, unit).valueOf();
            },
            diff: function (max, min, unit) {
                return moment(max).diff(moment(min), unit);
            },
            startOf: function (time, unit, weekday) {
                time = moment(time);
                if (unit === 'isoWeek') {
                    weekday = Math.trunc(Math.min(Math.max(0, weekday), 6));
                    return time.isoWeekday(weekday).startOf('day').valueOf();
                }
                return time.startOf(unit).valueOf();
            },
            endOf: function (time, unit) {
                return moment(time).endOf(unit).valueOf();
            }
        } : {};
        return output;
    }
    static GetDefaultDatePickerConfig(hasTime = false) {
        return {
            dateFormat: hasTime ? "d M Y H:i" : "d M Y",
            wrap: true,
            enableTime: hasTime,
            allowInput: true,
            parseDate: DateHelper.parseDateInput
        };
    }
    ;
    static parseDateInput(inputValue, format) {
        if (!inputValue || inputValue.length < 8) {
            return null;
        }
        let dateFormats = ["DD-MMM-YYYY", "DD-MM-YYYY", "YYYY-MMM-DD", "YYYY-MM-DD"];
        let dateTimeFormats = [];
        dateFormats.forEach(item => dateTimeFormats.push(item + " HH:mm"));
        dateFormats = dateFormats.concat(dateTimeFormats);
        try {
            return moment(inputValue, dateFormats).toDate();
        }
        catch (ex) {
            console.error("Error parsing Date/Time", ex);
            return null;
        }
    }
    static getDateFilterOptionTitle(option) {
        switch (option) {
            case "W": return "Filter previous 7 days";
            case "M": return "Filter previous 30 days";
            case "12 Weeks": return "Filter previous 12 weeks";
            case "3M": return "Filter previous 3 months";
            case "Y": return "Filter last year";
            case "2Y": return "Filter last 2 years";
            case "All": return "No date filter is applied";
            case "Prev M": return "Filter the previous month, staring from the first day of the month and ending on the last day";
            case "...": return "Define a custom period to be filtered";
            default: return "";
        }
    }
}
DateHelper.dateFormat = "YYYY-MM-DD HH:mm:ss";
